import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { resolveCollectorContributionContext } from '@/lib/community-collector.storage';
import { kitLang } from '@/features/contribution-kit';
import {
  InitialDataSubNav,
  InitialDataWorkspace,
  TextAnswerPanel,
  initialDataCopy,
  textSubmitFeedback,
  useInitialTextSession,
} from '@/features/initial-data';
import { CollectionPlaceStep } from '@/features/collection-place';

/** "Texte initial": choose the place, a language and a sector, then answer one question at a time in writing. */
const InitialText = () => {
  const { language } = useApp();
  const copy = initialDataCopy[kitLang(language)];
  const { toast } = useToast();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const collectorContext = useMemo(() => resolveCollectorContributionContext(location.state), [location.state]);

  const { setup, catalog, session, answer, updateAnswer, pause, isSubmitting, startSession, submitCurrent } =
    useInitialTextSession(collectorContext);

  const hasResponse = answer.response.trim().length > 0;

  const handleSubmit = async () => {
    if (!hasResponse) {
      toast({ title: copy.textRequired, variant: 'destructive' });
      return;
    }
    toast(textSubmitFeedback(copy, await submitCurrent()));
  };

  const handlePause = () => {
    pause.pause();
    toast({ title: copy.pausedToast, description: copy.pausedToastBody });
  };

  return (
    <>
      {/* The four initial-data sub-modules, offered before a session starts. */}
      {setup.step === 'setup' && <InitialDataSubNav />}
      <InitialDataWorkspace
        copy={copy}
        mode="text"
        step={setup.step}
        languages={setup.languages}
        isLoadingLanguages={setup.isLoadingLanguages}
        language={setup.language}
        languageName={setup.languageName}
        sectorId={setup.sectorId}
        placeStep={<CollectionPlaceStep state={setup.collectionPlace} />}
        isPlaceReady={setup.collectionPlace.place !== null}
        catalog={catalog}
        session={session}
        isSubmitting={isSubmitting}
        canSubmit={hasResponse}
        blocked={false}
        collectorProfileCode={collectorContext?.communityProfileCode}
        answerPanel={
          <TextAnswerPanel
            key={session.current?.id ?? 'none'}
            copy={copy}
            language={setup.language}
            languageName={setup.languageName}
            answer={answer}
            disabled={isSubmitting || session.isCurrentDone}
            onChange={updateAnswer}
          />
        }
        onLanguageChange={setup.setLanguage}
        onSectorChange={setup.setSectorId}
        onStart={startSession}
        onSubmit={() => void handleSubmit()}
        onChangeSector={() => setup.setStep('setup')}
        savedPosition={pause.saved}
        locale={language}
        onResume={pause.resume}
        onRestart={pause.restart}
        onPause={handlePause}
        onLeave={() => navigate(`/${lang || 'fr'}`)}
      />
    </>
  );
};

export default InitialText;
